import { supabase } from '../lib/supabase';

export type TipoManifestacao = 'sugestao' | 'reclamacao' | 'denuncia' | 'elogio' | 'solicitacao_informacao' | 'outro';
export type ModoIdentificacao = 'identificada' | 'sigilosa' | 'anonima';

export const TIPO_MANIFESTACAO_LABEL: Record<TipoManifestacao, string> = {
  sugestao: 'Sugestão',
  reclamacao: 'Reclamação',
  denuncia: 'Denúncia',
  elogio: 'Elogio',
  solicitacao_informacao: 'Solicitação de informação',
  outro: 'Outro',
};

export const MODO_IDENTIFICACAO_LABEL: Record<ModoIdentificacao, string> = {
  identificada: 'Identificada',
  sigilosa: 'Sigilosa',
  anonima: 'Anônima',
};

export interface AssuntoOuvidoria {
  id: string;
  nome: string;
  descricao: string | null;
  area_id: string | null;
}

export const ouvidoriaPortalService = {
  async listarAssuntos(estabelecimentoId: string): Promise<AssuntoOuvidoria[]> {
    const { data, error } = await supabase
      .from('assuntos')
      .select('id, nome, descricao, area_id')
      .eq('estabelecimento_id', estabelecimentoId)
      .eq('modulo_codigo', 'ouvidoria')
      .eq('publica', true)
      .eq('ativo', true)
      .is('deletado_em', null)
      .order('nome');
    if (error) throw error;
    return (data ?? []) as AssuntoOuvidoria[];
  },

  // Para usuario logado (cidadao identificado)
  async abrirIdentificada(args: {
    estabelecimento_id: string;
    cidadao_id: string;
    assunto_id: string;
    tipo: TipoManifestacao;
    titulo: string;
    descricao: string;
    sigilosa: boolean;
    endereco?: string;
    cep?: string;
    bairro?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<{ id: string; numero: string; hash_publico: string }> {
    const { data, error } = await supabase
      .from('documentos')
      .insert({
        estabelecimento_id: args.estabelecimento_id,
        modulo_codigo: 'ouvidoria',
        assunto_id: args.assunto_id,
        titulo: args.titulo,
        descricao: args.descricao,
        status: 'aberto',
        prioridade: 'media',
        visibilidade: args.sigilosa ? 'restrita_lista' : 'interna',
        modo_identificacao: args.sigilosa ? 'sigilosa' : 'identificada',
        tipo_manifestacao: args.tipo,
        solicitante_cidadao_id: args.cidadao_id,
        endereco: args.endereco,
        cep: args.cep,
        bairro: args.bairro,
        latitude: args.latitude,
        longitude: args.longitude,
      })
      .select('id, numero, hash_publico')
      .single();
    if (error) throw error;
    return data as { id: string; numero: string; hash_publico: string };
  },

  // Anonima — via RPC (não exige login)
  async abrirAnonima(args: {
    estabelecimento_id: string;
    assunto_id: string;
    tipo: TipoManifestacao;
    titulo: string;
    descricao: string;
    endereco?: string;
    cep?: string;
    bairro?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<{ numero: string; hash_publico: string }> {
    const { data, error } = await supabase.rpc('ouvidoria_abrir_anonima', {
      p_estabelecimento_id: args.estabelecimento_id,
      p_assunto_id: args.assunto_id,
      p_tipo_manifestacao: args.tipo,
      p_titulo: args.titulo,
      p_descricao: args.descricao,
      p_endereco: args.endereco ?? null,
      p_cep: args.cep ?? null,
      p_bairro: args.bairro ?? null,
      p_latitude: args.latitude ?? null,
      p_longitude: args.longitude ?? null,
    });
    if (error) throw error;
    if (data && typeof data === 'object' && 'erro' in data) {
      throw new Error(String((data as { erro: string }).erro));
    }
    return data as { numero: string; hash_publico: string };
  },

  // Avaliação publica (token)
  async lerAvaliacao(token: string): Promise<unknown> {
    const { data, error } = await supabase.rpc('ouvidoria_ler_avaliacao', { p_token: token });
    if (error) throw error;
    return data;
  },

  async enviarAvaliacao(token: string, args: { estrelas: number; comentario?: string; resolveu?: boolean }): Promise<void> {
    const { data, error } = await supabase.rpc('ouvidoria_avaliar_publico', {
      p_token: token,
      p_estrelas: args.estrelas,
      p_comentario: args.comentario ?? null,
      p_resolveu: args.resolveu ?? null,
      p_ip: null,
      p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
    if (error) throw error;
    if (data && typeof data === 'object' && 'erro' in data) {
      throw new Error(String((data as { erro: string }).erro));
    }
  },
};
